import { z } from 'zod';
import { paginated, paginationQuerySchema } from './common.js';
import { normalisedEmailSchema } from './tenant.js';
/**
 * Tenant-administration contracts.
 *
 * The vocabulary is deliberately "member", not "user": a tenant administrator
 * administers MEMBERSHIPS of a global identity, never the global identity
 * itself (ADR 0009). Platform administrators manage users; tenant
 * administrators manage members.
 */
/**
 * The assignable role set. PLATFORM_ADMIN is absent by construction — it is a
 * platform capability on User, not a Role row, so it is not expressible here
 * at all. A tenant API therefore cannot assign platform-level privilege even
 * if a caller tries.
 */
export const assignableTenantRoleSchema = z.enum([
    'TENANT_ADMIN',
    'OPERATIONS_MANAGER',
    'OPERATOR',
    'VIEWER',
]);
export const membershipStatusSchema = z.enum(['ACTIVE', 'INACTIVE']);
// ── members ─────────────────────────────────────────────────────────────────
export const adminMemberListQuerySchema = paginationQuerySchema
    .extend({
    sort: z.enum(['displayName', 'email', 'createdAt']).default('displayName'),
    direction: z.enum(['asc', 'desc']).default('asc'),
    search: z.string().trim().min(1).optional(),
    status: membershipStatusSchema.optional(),
    role: assignableTenantRoleSchema.optional(),
})
    .strict();
/**
 * What a tenant administrator may see about a member.
 *
 * Deliberately ABSENT:
 *  - `isPlatformAdmin` — platform staffing is not a tenant's business;
 *  - any other membership — a tenant must not learn where else an identity
 *    belongs (ADR 0009 §2, and requirement "do not expose whether a user
 *    belongs to another tenant").
 *
 * `status` is the MEMBERSHIP status. The global `User.status` is not surfaced,
 * because a tenant administrator cannot act on it and it would leak
 * platform-level lifecycle state.
 */
export const adminMemberSchema = z.object({
    userId: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    role: assignableTenantRoleSchema,
    /** The MEMBERSHIP. Deactivating it leaves the person's account alone (D1). */
    status: membershipStatusSchema,
    /**
     * The ACCOUNT. `INACTIVE` means they cannot sign in AT ALL, anywhere.
     *
     * ⚠️ Carried because the client cannot apply a rule whose input it was never
     * sent — the same reason `tenantMembershipDto` carries `tenantState`. Without
     * it a member deactivated platform-wide reads as a perfectly ordinary ACTIVE
     * member, and the administrator wondering why they cannot sign in has no way
     * to find out from the screen built to answer it.
     *
     * A plain string rather than an enum, matching `PartnerMemberDto`: this is a
     * global lifecycle a tenant surface only REPORTS. Widening it later must not
     * be a breaking change here, and a value this list has not caught up with is
     * still real.
     */
    accountStatus: z.string(),
    /** When this identity joined THIS tenant — not when the account was created. */
    joinedAt: z.string(),
    updatedAt: z.string(),
});
export const adminMemberListResponseSchema = paginated(adminMemberSchema);
/**
 * Add an existing global identity to this tenant.
 *
 * There is no password field and no displayName field: a tenant administrator
 * cannot create a global identity (ADR 0009 §2), so there is nothing to set.
 * If the address has no eligible account, the request fails and the platform
 * must provision the user first.
 *
 * `tenantId` is absent by construction — it comes from the request context.
 */
export const createMembershipRequestSchema = z
    .object({
    email: normalisedEmailSchema,
    role: assignableTenantRoleSchema,
})
    .strict();
/**
 * Update a membership. Role is the only tenant-owned attribute of a member.
 *
 * Global fields (`email`, `displayName`) are intentionally NOT updatable here:
 * they belong to a global identity shared with other tenants, so a tenant
 * administrator editing them would silently change what every other tenant
 * sees. Per-tenant profile attributes do not exist in the current model.
 */
export const updateMembershipRequestSchema = z
    .object({ role: assignableTenantRoleSchema })
    .strict();
// ── roles + permissions catalogue ───────────────────────────────────────────
export const adminRoleSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    description: z.string().nullable(),
    permissions: z.array(z.string()),
    /** False for roles that exist but may not be assigned within a tenant. */
    assignable: z.boolean(),
});
export const adminRolesResponseSchema = z.object({ roles: z.array(adminRoleSchema) });
export const adminPermissionsResponseSchema = z.object({
    permissions: z.array(z.object({ id: z.string().uuid(), name: z.string() })),
});
// ── provider connections ────────────────────────────────────────────────────
export const providerConnectionSchema = z.object({
    id: z.string().uuid(),
    providerId: z.string(),
    displayName: z.string(),
    baseUrl: z.string().nullable(),
    /** The key the PROVIDER presents to US. A reference, never the secret. */
    credentialRef: z.string(),
    /**
     * The key WE present to THEM. Also a reference, and deliberately a separate
     * one — reusing the inbound key for outbound calls would send a customer's
     * own credential to a third party.
     */
    outboundCredentialRef: z.string().nullable(),
    /**
     * REFERENCE | STORED | null — where the INBOUND key lives.
     *
     * `null` means REFERENCE, which is how every connection worked before sealed
     * storage existed. The secret is never in this response in either mode, for
     * the same reason the outbound one is not: a field that can be read is a field
     * that ends up in a log, a screenshot or a support ticket.
     */
    inboundAuthMode: z.enum(['REFERENCE', 'STORED']).nullable(),
    /** When the key was last set or rotated. The value is never recorded. */
    inboundSecretSetAt: z.string().nullable(),
    /**
     * REFERENCE | STORED | null — how the outbound credential is supplied.
     *
     * The SECRET itself is never in this response, in either mode. A field that
     * can be read is a field that ends up in a log, a screenshot or a support
     * ticket, so the screen says "configured" and offers to replace it.
     */
    outboundAuthMode: z.enum(['REFERENCE', 'STORED']).nullable(),
    /**
     * BASIC | BEARER | API_KEY — how the credential is presented on the wire.
     *
     * A choice rather than an inference: AjnaView supports all three, so deriving
     * it from whether a username was typed would make the wire format a side
     * effect of which box somebody filled in.
     */
    outboundAuthScheme: z.enum(['BASIC', 'BEARER', 'API_KEY']),
    /** The login half, when one is configured. An identifier, not a secret. */
    outboundUsername: z.string().nullable(),
    /** When the credential last PROVED it works, by completing a real pull. */
    outboundVerifiedAt: z.string().datetime().nullable(),
    outboundVerifyError: z.string().nullable(),
    /** Whether zone definitions are pulled from this provider on a schedule. */
    geofenceSyncEnabled: z.boolean(),
    /**
     * Last SUCCESS and last ATTEMPT, kept apart on purpose. One field would let a
     * sync that has failed every run this week hide behind a recent timestamp.
     */
    geofenceSyncedAt: z.string().datetime().nullable(),
    geofenceSyncAttemptedAt: z.string().datetime().nullable(),
    /** The provider's own words when the last attempt failed. */
    geofenceSyncError: z.string().nullable(),
    status: z.string(),
    config: z.record(z.unknown()),
    createdAt: z.string(),
});
/**
 * Configuring outbound calls to a provider.
 *
 * A sync cannot be enabled without somewhere to call and something to
 * authenticate with — refused here, and again by a database CHECK, because an
 * enabled sync with no target fails every run and reads as a provider outage
 * rather than as configuration nobody finished.
 */
export const updateProviderOutboundSchema = z
    .object({
    baseUrl: z.string().url().max(500).nullable().optional(),
    /** A REFERENCE resolved through the secrets abstraction, never a secret. */
    outboundCredentialRef: z.string().trim().min(1).max(120).nullable().optional(),
    outboundAuthScheme: z.enum(['BASIC', 'BEARER', 'API_KEY']).optional(),
    geofenceSyncEnabled: z.boolean().optional(),
})
    .strict();
/**
 * A credential the TENANT supplies, which OIP seals and stores.
 *
 * Separate from the schema above on purpose: that one carries configuration a
 * screen may safely re-send on every save, and this one carries a secret. Mixing
 * them would mean the form posting the password back every time somebody
 * toggled the sync — and a secret that travels more often than it changes is a
 * secret with more chances to be logged.
 */
export const storeProviderCredentialSchema = z
    .object({
    /**
     * Present for a login, absent for a bare key or token.
     *
     * The pair is assembled server-side. Asking an administrator to base64 a
     * `user:pass` string by hand is a step they would get wrong, and splitting
     * one we stored would corrupt any password containing a colon.
     */
    username: z.string().trim().min(1).max(200).nullable().optional(),
    secret: z.string().min(1).max(4000),
})
    .strict();
/**
 * The result of a sync run on demand.
 *
 * Reports what it DID — created, updated, withdrawn — rather than "success",
 * because the number that matters to somebody pressing the button is whether
 * zones actually arrived. A run that succeeds and applies nothing is a
 * configuration problem wearing a green tick.
 */
export const providerSyncResultSchema = z.object({
    ok: z.boolean(),
    created: z.number().int(),
    updated: z.number().int(),
    withdrawn: z.number().int(),
    /** The provider's own words when it failed. Null on success. */
    error: z.string().nullable(),
});
/**
 * Create a provider connection, and mint the key the provider will present.
 *
 * The key is GENERATED here rather than supplied. A customer-chosen inbound
 * credential is a customer-chosen password on a data ingestion route, and the
 * one thing this endpoint exists to remove is a human choosing it.
 */
export const createProviderConnectionRequestSchema = z
    .object({
    /** Must have a registered adapter, or nothing could map what arrives. */
    providerId: z.string().trim().min(1).max(64),
    displayName: z.string().trim().min(1).max(120),
    /** Only needed for outbound calls; ingestion never uses it. */
    baseUrl: z.string().url().max(2048).optional(),
})
    .strict();
/**
 * The one and only time the key is returned.
 *
 * Shown once and never again — it is sealed at rest and no endpoint reads it
 * back, so the screen must make clear that closing the panel loses it. The
 * alternative, a key that can be re-read, is a key that ends up in a support
 * ticket.
 */
export const providerConnectionSecretResponseSchema = z.object({
    connection: providerConnectionSchema,
    /** Shown once. Never retrievable again — generate a new one instead. */
    apiKey: z.string(),
});
export const providerConnectionsResponseSchema = z.object({
    connections: z.array(providerConnectionSchema),
});
// ── tenant + llm settings ───────────────────────────────────────────────────
export const tenantSettingSchema = z.object({ key: z.string(), value: z.string() });
export const adminTenantSettingsResponseSchema = z.object({
    tenantId: z.string().uuid(),
    settings: z.array(tenantSettingSchema),
});
export const llmSettingsResponseSchema = z.object({
    tenantId: z.string().uuid(),
    tenantLlmEnabled: z.boolean(),
    settings: z.array(tenantSettingSchema),
});
/**
 * The operating calendar — which days this organisation's fleet works.
 *
 * ⚠️ **PARENT-owned, and the response says so rather than leaving a branch
 * administrator to discover it from a 400.** `operating.*` is a parent-owned
 * setting prefix, because a calendar is the ORGANISATION's: two branches
 * disagreeing about Saturday would give one vehicle two different normals
 * depending on which branch held it, and a transfer would silently change what
 * counts as unusual about it. `editable` is the server's answer to "may I set
 * this here", so the screen never offers a control the API refuses.
 */
export const operatingCalendarSchema = z.object({
    /** ISO weekday numbers, Monday = 1 … Sunday = 7. */
    workingDays: z.array(z.number().int().min(1).max(7)),
    /**
     * ⚠️ False where the tenant is INHERITING the Monday–Friday default rather
     * than having chosen it. The two produce identical behaviour and are different
     * facts: a reader who cannot tell them apart does not know whether anybody has
     * looked at this, which is the `sources` rule the units card already follows.
     */
    configured: z.boolean(),
    /** True only in the tenant that OWNS the setting — the organisation. */
    editable: z.boolean(),
    /** The organisation this calendar belongs to, so a branch can be sent there. */
    ownerTenantId: z.string().uuid(),
    ownerName: z.string(),
});
export const updateOperatingCalendarSchema = z
    .object({
    /**
     * ⚠️ **At least one day, refused on the server.** A fleet with no working
     * days has no normal to compare against, so every observation would be
     * measured against the non-working baseline and the whole fleet would read
     * as anomalous on every day it worked. Far likelier a mistake than an
     * intent — the same refusal `parseWorkingDays` already makes when reading.
     */
    workingDays: z.array(z.number().int().min(1).max(7)).min(1).max(7),
})
    .strict();
/**
 * Whether this organisation's data may reach a model hosted outside the platform.
 *
 * Two facts, deliberately separate, because they fail differently and need
 * different people: the PLATFORM grants `AI_EXTERNAL_PROVIDER` (the ceiling
 * permits) and the ORGANISATION switches `llm.allowExternalProvider` on (the
 * customer opts in). Either alone sends nothing.
 *
 * ⚠️ **PARENT-owned, like the calendar above and for a stronger reason.** This is a
 * data-egress decision about the whole organisation, so a branch administrator must
 * not be able to consent on its behalf from a page that never says whose decision
 * it is — which is the `allowExternalProvider` defect this codebase already names as
 * the archetype. `editable` is the server's answer to "may I set this here".
 */
export const aiConsentSchema = z.object({
    /** The stored consent — `llm.allowExternalProvider`, absent meaning false. */
    consented: z.boolean(),
    /** Whether the platform has granted `AI_EXTERNAL_PROVIDER` to this organisation. */
    entitled: z.boolean(),
    /**
     * ⚠️ The ONLY field that says whether anything actually leaves: consent AND
     * entitlement AND a provider that is genuinely external. Derived on the server so
     * the screen cannot assemble a different answer from the parts — and it is what a
     * reader is really asking.
     */
    effective: z.boolean(),
    /**
     * The provider a model call would reach right now, and whether it is outside the
     * platform.
     *
     * ⚠️ **Named, never described as "external".** Consent to *something outside* is
     * not consent; a reader has to know WHO. And `external: false` — a local or mock
     * provider — is why the switch can be on and change nothing, which the screen must
     * say rather than leaving a control that appears to do something.
     */
    provider: z.object({ id: z.string(), model: z.string(), external: z.boolean() }),
    /**
     * WHO grants the entitlement, when this organisation does not hold it.
     *
     * ⚠️ **NOT always the platform.** A company sold through a white-label partner is
     * entitled by its PARTNER, and telling that customer to "ask your platform
     * administrator" sends them to somebody who will not discuss another company's
     * contract with them. The `NoUsableTenantPage` lesson, which already names the
     * organisation's administrator for a branch and the platform's only for a
     * top-level tenant.
     *
     * ⚠️ **NULL means the PLATFORM, never "nobody".** Every tenant that came to OIP
     * directly returns null here — the `partnerOf` shape — so reading it as "no route
     * to an entitlement" would leave the commonest customer told nobody can help.
     */
    grantedBy: z.string().nullable(),
    /** True only in the tenant that OWNS the setting — the organisation. */
    editable: z.boolean(),
    ownerTenantId: z.string().uuid(),
    ownerName: z.string(),
});
export const updateAiConsentSchema = z.object({ consented: z.boolean() }).strict();
/**
 * The kinds of anomaly the detector looks for, and which a tenant has switched
 * off.
 *
 * ⚠️ **The list is the CATALOGUE, never "what was found".** The findings grid
 * offers only the families PRESENT in a window, deliberately — but that means a
 * quiet week reads as a detector that checks for almost nothing. This is the
 * answer to *what does the system look for*, and it is the same six whether or
 * not any of them fired.
 */
export const anomalyFamilySettingSchema = z.object({
    key: z.string(),
    label: z.string(),
    description: z.string(),
    /** True where this tenant has switched the kind off. */
    muted: z.boolean(),
});
export const anomalyFamilySettingsSchema = z.object({
    families: z.array(anomalyFamilySettingSchema),
    /** True only in the tenant that OWNS the setting — the organisation. */
    editable: z.boolean(),
    ownerTenantId: z.string().uuid(),
    ownerName: z.string(),
});
export const updateAnomalyFamiliesSchema = z
    .object({
    /**
     * ⚠️ The MUTED set, not the enabled one — see `ANOMALY_MUTED_FAMILIES_KEY`.
     * An empty array is a real value meaning "raise all of them", which is why
     * there is no minimum. Muting every family is ALLOWED: it is the honest way
     * to say "measure but tell me nothing", and refusing it would leave a tenant
     * who wants that reaching for the publication mode instead, which is a
     * platform control they do not have.
     */
    muted: z.array(z.string()).max(64),
})
    .strict();
/**
 * What a provider is CALLED, for a screen.
 *
 * ⚠️ **A provider id is a URL SEGMENT and a stored value — lowercase, opaque,
 * and not a name.** `/api/v1/providers/:providerId` selects a namespace, so the
 * id is the platform's vocabulary; rendering it raw puts `ajnaview` and
 * `wialon` on the one screen a customer reads to check which system their
 * telemetry comes from. The same complaint as every other enum this programme
 * has turned into words.
 *
 * ⚠️ **An unrecognised id renders from its OWN id, never a placeholder.** Any
 * provider with an adapter can be registered, and a customer running one this
 * table has not caught up with must still see which connection is which —
 * `ajnaview-eu` is ugly and true, "Unknown" is neither. The `policyLabel` rule.
 *
 * ⚠️ **DISPLAY only.** The id stays what the API accepts, what the ingestion URL
 * carries and what an integrator reads in the spec — this never renames it.
 */
export const PROVIDER_LABELS = {
    ajnaview: 'AjnaView',
    wialon: 'Wialon',
    generic: 'Generic',
};
export function providerLabel(providerId) {
    return PROVIDER_LABELS[providerId] ?? providerId;
}
//# sourceMappingURL=admin.js.map